import React, { useEffect, useState } from "react";
import PrivateLayout from "layouts/private/private";
import {
  ActionButton,
  FocusZone,
  FocusZoneDirection,
  IconButton,
  IIconProps,
  IList,
  ITheme,
  List,
  mergeStyleSets,
  MessageBar,
  MessageBarType,
  normalize,
  TextField,
  useTheme,
} from "@fluentui/react";
import { extractErrorMessage } from "@helpers/alertUtils";
import { ProductApi } from "@api/productsApi";
import { IProductInfo } from "@models/dto/Rocketta/LicenseServer/Models/Products/IProductInfo";
import { ProductModal } from "./ProductModal";
import { useBoolean } from "@fluentui/react-hooks";
import { NewLicenseModal } from "./NewLicenseModal";

const numberOfItemsOnPage = 50;
const itemHeight = 25;
const generateStyles = (theme: ITheme) => {
  return mergeStyleSets({
    container: {
      overflow: "auto",
      maxHeight: 400,
      border: "1px solid " + theme.palette.neutralLight,
      marginTop: 20,
      selectors: {
        ".ms-List-cell .odd": {
          height: itemHeight,
          lineHeight: itemHeight,
        },
        ".ms-List-cell .even": {
          height: itemHeight,
          lineHeight: itemHeight,
          background: theme.palette.neutralLighter,
        },
      },
    },
    itemWrapper: {
      display: "flex",
      alignItems: "center",
    },
    itemContent: [
      theme.fonts.medium,
      normalize,
      {
        position: "relative",
        boxSizing: "border-box",
        display: "block",
        borderLeft: "3px solid " + theme.palette.themePrimary,
        paddingLeft: 27,
        paddingRight: 5,
      },
    ],
  });
};

const licensesIcon: IIconProps = { iconName: "Permissions" };
const editIcon: IIconProps = { iconName: "Edit" };

export const Products: React.FC = () => {
  const [products, setProducts] = useState<IProductInfo[]>([]);
  const [items, setItems] = useState<IProductInfo[]>([]);
  const [errorMessage, setErrorMessage] = useState<string | undefined>();
  const [productModalVisible, { setTrue: showProductModal, setFalse: hideProductModal }] = useBoolean(false);
  const [newLicenseModalVisible, { setTrue: showNewLicenseModal, setFalse: hideNewLicenseModal }] = useBoolean(false);
  const [currentProduct, setCurrentProduct] = useState<IProductInfo | undefined>(undefined);
  const listRef: React.RefObject<IList> = React.useRef(null);
  const theme = useTheme();
  const classNames = React.useMemo(() => generateStyles(theme), [theme]);

  const addProductIcon: IIconProps = { iconName: "AddTo", color: theme.palette.neutralPrimary };

  useEffect(() => {
    ProductApi.getProducts({})
      .then((result) => {
        const productsItems = result.data.data;
        setProducts(productsItems);
        setItems(productsItems);
        return productsItems;
      })
      .catch((err) => {
        const message = extractErrorMessage(err);
        setErrorMessage(message);
      });
  }, []);

  const getPageHeight = (idx: number): number => {
    let h = 0;
    for (let i = idx; i < idx + numberOfItemsOnPage; ++i) {
      h += itemHeight;
    }
    return h;
  };

  const onRenderCell = React.useCallback(
    (item: IProductInfo, index: number): JSX.Element => {
      return (
        <div data-is-focusable className={classNames.itemWrapper}>
          <div className={classNames.itemContent}>
            {item.productId} &nbsp; {item.name}
          </div>
          <IconButton
            iconProps={licensesIcon}
            onClick={() => {
              setCurrentProduct(item);
              showNewLicenseModal();
            }}
          />
          <IconButton
            iconProps={editIcon}
            onClick={() => {
              setCurrentProduct(item);
              showProductModal();
            }}
          />
        </div>
      );
    },
    [classNames]
  );

  const onFilterChanged = (_: any, text: string): void => {
    setItems(
      products.filter(
        (item) =>
          item.name.toLowerCase().indexOf(text.toLowerCase()) >= 0 ||
          item.productId.toLowerCase().indexOf(text.toLowerCase()) >= 0
      )
    );
  };

  return (
    <PrivateLayout>
      {errorMessage && (
        <MessageBar messageBarType={MessageBarType.error} isMultiline={true} dismissButtonAriaLabel="Close">
          {errorMessage}
        </MessageBar>
      )}
      <h1>Products</h1>
      <ActionButton
        iconProps={addProductIcon}
        size={50}
        allowDisabledFocus
        disabled={false}
        checked={false}
        onClick={() => {
          setCurrentProduct(undefined);
          showProductModal();
        }}
      >
        Create product
      </ActionButton>
      <FocusZone direction={FocusZoneDirection.vertical}>
        <TextField
          label={"Filter"}
          // eslint-disable-next-line react/jsx-no-bind
          onChange={onFilterChanged}
        />
        <List componentRef={listRef} items={items} getPageHeight={getPageHeight} onRenderCell={onRenderCell} />
      </FocusZone>
      <ProductModal
        productId={currentProduct?.id}
        productModalVisible={productModalVisible}
        hideProductModal={() => {
          setCurrentProduct(undefined);
          hideProductModal();
        }}
      />
      {currentProduct && (
        <NewLicenseModal
          productId={currentProduct.id}
          newLicenseModalVisible={newLicenseModalVisible}
          hideNewLicenseModal={hideNewLicenseModal}
        />
      )}
    </PrivateLayout>
  );
};
